//#region IMPORTS
import {
  downloadErrorsExcel,
  ErrorRow,
} from "../../helpers/downloadErrorsExcel";
import { FieldValues, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Promo } from "../../models/promo";
import LoadingData from "../loading/LoadingData";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  setSavingHandler: (value: boolean) => void;
  cancelCUD: () => void;
  saving?: boolean;
  promo?: Promo;
}

// The import endpoint answers a 422 with `missing` in two different shapes:
// an object when whole sheets or columns are absent, and a flat list of values
// for missing stores (PICKLISTS -> STORE_ID) or missing product versions
// (DOCUMENT_LANDCODE). For the missing stores it also writes the spreadsheet
// itself and returns a temporary download URL in `excel`.
type MissingPayload =
  | (string | number)[]
  | {
      data?: {
        sheets?: string[];
        headers?: Record<string, string[]>;
      };
    };

interface PromoImportError {
  message: string;
  missing?: MissingPayload;
  excel?: string | null;
}
//#endregion

export default function PromosDataUpload({
  setSavingHandler,
  cancelCUD,
  saving = false,
  promo,
}: Props) {
  //#region SETUP
  // Local state
  const [uploadError, setUploadError] = useState<PromoImportError | null>(null);

  // UseForm
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({ mode: "all" });

  //#endregion

  //#region LOGIC
  async function submitForm(data: FieldValues) {
    let formData = new FormData();
    // VERY IMPORTANT - only way to acces the FileList in data object
    formData.append("file", data.excellFile[0]);

    if (promo) {
      try {
        setSavingHandler(true);
        await agent.Promos.createFromDataSource(formData, promo.id);
        toast.success("Data imported");
        setSavingHandler(false);
        cancelCUD();
      } catch (error: any) {
        // Only the 422 carries a body we can explain; a 500 or a dropped
        // connection has none, and reading it would throw in this catch.
        setUploadError(
          error?.response?.data ?? {
            message: "The import failed. Please try again.",
          }
        );
        setSavingHandler(false);
        toast.error(`${error}`);
      }
    } else toast.warning("no promo selected");
  }

  // One row per problem, whichever shape the endpoint used. Feeds both the list
  // on screen and the spreadsheet, so those two cannot drift apart.
  const missingRows = useMemo<ErrorRow[]>(() => {
    const missing = uploadError?.missing;
    if (!missing) return [];

    // Missing stores or product versions: a flat list of values.
    if (Array.isArray(missing))
      return missing.map((value) => ({ Missing: value }));

    const data = missing.data;
    if (!data) return [];

    const sheetRows = (data.sheets ?? []).map((sheet) => ({
      Problem: "Missing sheet",
      Sheet: sheet,
      Column: "",
    }));

    const columnRows = Object.entries(data.headers ?? {}).flatMap(
      ([sheet, columns]) =>
        columns.map((column) => ({
          Problem: "Missing column",
          Sheet: sheet,
          Column: column,
        }))
    );

    return [...sheetRows, ...columnRows];
  }, [uploadError]);

  const downloadErrorsHandler = () => {
    // For the missing stores the backend already built a spreadsheet and handed
    // us a temporary URL. That file holds the whole PICKLISTS row per store,
    // which is what you need to create them, so prefer it over our own list.
    if (uploadError?.excel) {
      window.open(uploadError.excel, "_blank");
      return;
    }

    downloadErrorsExcel(
      missingRows,
      `promo-import-errors${promo ? `-${promo.code}` : ""}`
    );
  };
  //#endregion

  //#region RENDER
  return (
    <div className="px-4 py-5 sm:p-6">
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        {saving ? "The data is uploading..." : "Upload promo data"}
      </h3>
      <div className="mt-2 max-w-xl text-sm text-gray-500">
        <p>
          {saving
            ? "This can take a few moments..."
            : "This wil upload the entire original order data."}
        </p>
      </div>

      {!saving && !uploadError ? (
        <form
          className="mt-5 flex justify-between sm:items-center"
          onSubmit={handleSubmit(submitForm)}
        >
          <div className="w-full sm:max-w-xs">
            <label htmlFor="excellFile" className="sr-only">
              {errors.excellFile
                ? errors?.excellFile?.message
                : "choose excell file"}
            </label>
            <input
              className="block w-full rounded-md pl-1 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-none"
              type="file"
              autoFocus
              placeholder="choose main promo excell"
              {...register("excellFile", {
                required: "excell file is required",
              })}
            />
          </div>
          <div>
            <button
              onClick={cancelCUD}
              type="button"
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 sm:ml-3 sm:mt-0 sm:w-auto"
            >
              {isSubmitting ? "importing..." : "import"}
            </button>
          </div>
        </form>
      ) : saving && !uploadError ? (
        <div className="flex items-center justify-center mb-8">
          <LoadingData />
        </div>
      ) : uploadError ? (
        <div>
          <button onClick={cancelCUD}>close</button>
          <h2 className="text-lg">{uploadError.message}</h2>

          {missingRows.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">
                {missingRows.length} problems found
              </p>

              {/* There can be hundreds, so scroll instead of growing the
                  modal past the screen. */}
              <table className="mt-1 block max-h-64 overflow-y-auto">
                <tbody>
                  {missingRows.map((row) => (
                    <tr key={Object.values(row).join("-")}>
                      {Object.values(row).map((value, index) => (
                        <td className="text-xs text-gray-600 pr-4" key={index}>
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(uploadError.excel || missingRows.length > 0) && (
            <div>
              <button
                onClick={downloadErrorsHandler}
                type="button"
                className="mt-3 inline-flex items-center justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500"
              >
                Download errors
              </button>

              {uploadError.excel && (
                <p className="mt-1 text-xs text-gray-500">
                  This link expires 30 minutes after the import attempt.
                </p>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
  //#endregion
}
